import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo } from 'react';

import { api, ApiError, endSession, startSession } from './api.js';
import { setLocale } from './lib/i18n.js';

const AuthContext = createContext(null);

/**
 * Who is signed in (`me`: a client or a staff member, or null) and the public facts every page needs
 * (company name, brand colours, Turnstile site key), from GET /api/public/bootstrap. After that, the
 * message catalogue of the person's language (GET /api/public/messages/auto: the client's or staff
 * member's choice, the visitor's cookie or browser language) so t() and Intl formats follow it.
 */
export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const bootstrap = useQuery({
    queryKey: ['bootstrap'],
    queryFn: async () => {
      try {
        return await api('/api/public/bootstrap');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return { me: null };
        throw err;
      }
    },
    staleTime: 60_000,
  });

  // Keyed by who is signed in: logging in or out can change the language.
  const who = bootstrap.data?.me
    ? `${bootstrap.data.me.client?.uuid ?? ''}:${bootstrap.data.me.staff?.uuid ?? ''}`
    : 'anon';
  const catalogue = useQuery({
    queryKey: ['messages', who],
    queryFn: async () => {
      try {
        return await api('/api/public/messages/auto');
      } catch {
        return null; // without the platform module (or offline): English
      }
    },
    enabled: Boolean(bootstrap.data),
    staleTime: 5 * 60_000,
  });
  const locale = catalogue.data?.locale ?? 'en';

  useEffect(() => {
    setLocale(locale);
    if (typeof document !== 'undefined') document.documentElement.lang = catalogue.data?.language ?? 'en';
  }, [locale, catalogue.data?.language]);

  // On an extra brand's hostname the browser tab carries the brand's name (index.html says Octbill).
  const brandName = bootstrap.data?.brand?.name;
  useEffect(() => {
    if (brandName && typeof document !== 'undefined') document.title = brandName;
  }, [brandName]);

  const value = useMemo(() => {
    const data = bootstrap.data ?? {};
    return {
      loading: bootstrap.isPending,
      error: bootstrap.error,
      me: data.me ?? null,
      company: data.company ?? { name: 'Octbill' },
      branding: data.branding ?? {},
      /** The extra brand this hostname shows (`{ uuid, name }`), or null for the default brand. */
      brand: data.brand ?? null,
      turnstile: data.turnstile ?? null,
      /** The cookie notice (on or off) and the privacy page it links to (Shell). */
      legal: data.legal ?? { privacy_url: null, cookie_notice: false },
      /** The addons the API runs (web/src/modules.js addonOn): their nav entries show. */
      addons: data.addons ?? [],
      messages: { ...(data.messages ?? {}), ...(catalogue.data?.messages ?? {}) },
      language: catalogue.data?.language ?? 'en',
      locale,
      languages: catalogue.data?.languages ?? [{ code: 'en', name: 'English', locale: 'en' }],
      /** Switches the language: saved on the account when signed in, else in a cookie. */
      async setLanguage(code) {
        const me = data.me;
        if (me?.client) await api('/api/client/language', { method: 'PATCH', body: { language: code } });
        else if (me?.staff) await api('/api/admin/account/language', { method: 'PATCH', body: { language: code } });
        else await api('/api/public/language', { method: 'POST', body: { language: code } });
        // Everything: store, KB and announcement text come back in the new language too.
        await queryClient.invalidateQueries();
      },
      refresh: () => queryClient.invalidateQueries({ queryKey: ['bootstrap'] }),
      async logout() {
        endSession();
        try {
          await api('/api/auth/logout', { method: 'POST' });
        } finally {
          startSession();
          await queryClient.resetQueries();
        }
      },
    };
  }, [bootstrap.data, bootstrap.isPending, bootstrap.error, catalogue.data, locale, queryClient]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
